import {
	formatAsMonth,
	formatAsWeekNumber,
} from "./helpers/date-formatting-util.js";
import { inGroupsOf } from "@foretagsplatsen/core-helpers/array";

function groupDays(days) {
	return {
		start: days[0].start,
		end: days.at(-1).end,
	};
}

function buildDate(day) {
	const start = new Date(day);
	start.setHours(0, 0, 0);
	const end = new Date(day);
	end.setHours(23, 59, 59);

	return {
		start,
		end,
	};
}

/**
 * Create sub period between two dates. This is required to build a valid fiscal year.
 *
 * @param {Date} start - Start date
 * @param {Date} end - End date
 * @param {object} [options] - Options
 * @param {boolean} options.includeBimesters - True if bimesters data should be generated
 * @return {object} - The sub periods models
 */
export default function buildSubPeriodsBetween(
	start,
	end,
	{ includeBimesters = false } = {},
) {
	const current = new Date(start);
	let previous = current;

	const result = {
		dayData: [],
		weekData: [],
		monthData: [],
	};

	let weekAccumulator = [];
	let monthAccumulator = [];

	while (current <= end) {
		const dateToAdd = buildDate(current);
		if (formatAsWeekNumber(previous) !== formatAsWeekNumber(current)) {
			result.weekData.push(groupDays(weekAccumulator));
			weekAccumulator = [];
		}
		if (formatAsMonth(previous) !== formatAsMonth(current)) {
			result.monthData.push(groupDays(monthAccumulator));
			monthAccumulator = [];
		}

		result.dayData.push(dateToAdd);
		weekAccumulator.push(dateToAdd);
		monthAccumulator.push(dateToAdd);

		// Update
		previous = new Date(current);
		current.setDate(current.getDate() + 1);
	}

	result.weekData.push(groupDays(weekAccumulator));
	result.monthData.push(groupDays(monthAccumulator));

	result.quarterData = inGroupsOf({
		array: result.monthData,
		groupSize: 3,
		padding: {},
	}).map((months) => ({
		start: months[0].start,
		end: months[2].end,
	}));

	if (includeBimesters) {
		result.bimestersData = inGroupsOf({
			array: result.monthData,
			groupSize: 2,
			padding: {},
		}).map((months) => ({ start: months[0].start, end: months[1].end }));
	}

	result.halfYearData = inGroupsOf({
		array: result.quarterData,
		groupSize: 2,
		padding: {},
	}).map((quarters) => ({
		start: quarters[0].start,
		end: quarters[1].end,
	}));

	return result;
}
